'use client';

import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import { MarkdownPreview } from '../../../../markdown-preview';
import { AdEntitiesListBody } from './ad-entities-list-body';
import { AdGraphListBody } from './ad-graph-list-body';
import {
  parseMcpBloodhoundEntitiesOutput,
  parseMcpBloodhoundGraphOutput,
  parseMcpBloodhoundJsonOutput,
} from './parse-mcp-bloodhound-output.util';
import type { ParsedAdEntity } from './parse-ad-entities-output.util';
import type { ParsedAdGraphOutput } from './parse-ad-graph-output.util';

function renderStructuredMcpOutput(
  parsed: ParsedAdGraphOutput | ParsedAdEntity[],
) {
  if (Array.isArray(parsed)) {
    return <AdEntitiesListBody entities={parsed} />;
  }

  return <AdGraphListBody graph={parsed} />;
}

function countMcpResults(parsed: unknown): number | undefined {
  if (Array.isArray(parsed)) {
    return parsed.length;
  }

  if (parsed === null || typeof parsed !== 'object') {
    return undefined;
  }

  const record = parsed as Record<string, unknown>;

  if (typeof record.total === 'number') {
    return record.total;
  }

  if (typeof record.count === 'number') {
    return record.count;
  }

  const items = record.items ?? record.results ?? record.data;

  return Array.isArray(items) ? items.length : undefined;
}

function McpJsonResultSummary({ parsed }: { parsed: unknown }) {
  const t = useTranslations('designSystem.chat.externalNetworkMcpQueryTool');
  const resultCount = countMcpResults(parsed);

  if (resultCount === undefined) {
    return null;
  }

  return (
    <Box width="100%" fontSize="xs" color="fg.muted">
      {t('resultCount', { count: resultCount })}
    </Box>
  );
}

export function McpBloodhoundOutputBody({ output }: { output: string }) {
  const trimmed = output.trim();

  if (!trimmed) {
    return null;
  }

  const structured = parseMcpBloodhoundJsonOutput(trimmed);

  if (structured) {
    return renderStructuredMcpOutput(structured);
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      const graph = parseMcpBloodhoundGraphOutput(parsed);

      if (graph) {
        return <AdGraphListBody graph={graph} />;
      }

      const entities = parseMcpBloodhoundEntitiesOutput(parsed);

      if (entities) {
        return <AdEntitiesListBody entities={entities} />;
      }

      return <McpJsonResultSummary parsed={parsed} />;
    } catch {
      // fall through to markdown
    }
  }

  return (
    <Box width="100%" color="fg.muted">
      <MarkdownPreview source={trimmed} fontSize={12} compact />
    </Box>
  );
}
